import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Text,
  Flex,
  Box,
  Link,
} from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { EmptyState } from '../../../../shared/components';
import { appTranslationRef } from '../../../../core/i18n';
import {
  GITLAB_ANNOTATIONS,
  parseEnvironmentsAnnotation,
  GitLabEnvironment,
} from './gitlab.types';
import { useSourceLocation } from '../../hooks';
import styles from './gitlab.module.css';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'success':
      return '#66bb6a';
    case 'running':
    case 'pending':
    case 'created':
      return '#ffa726';
    case 'failed':
    case 'canceled':
      return '#f44336';
    default:
      return 'rgba(255, 255, 255, 0.4)';
  }
}

const StatusDot = ({ status }: { status: string }) => (
  <span
    className={styles.statusDot}
    style={{ backgroundColor: getStatusColor(status) }}
  />
);

const DeploymentMeta = ({ deployment }: { deployment: NonNullable<GitLabEnvironment['lastDeployment']> }) => {
  const parts: React.ReactNode[] = [];

  parts.push(<code key="commit">{deployment.commitShortId}</code>);

  parts.push(
    <Link key="user" href={`/catalog/default/user/${deployment.username}`}>
      {deployment.username}
    </Link>,
  );

  if (deployment.pipelineId) {
    parts.push(
      deployment.pipelineUrl ? (
        <Link key="pipeline" href={deployment.pipelineUrl} target="_blank">#{deployment.pipelineId}</Link>
      ) : (
        <span key="pipeline">#{deployment.pipelineId}</span>
      ),
    );
  }

  parts.push(<span key="date">{formatDate(deployment.finishedAt || deployment.createdAt)}</span>);

  return (
    <Text variant="body-small" color="secondary">
      {parts.reduce<React.ReactNode[]>((acc, part, i) => {
        if (i > 0) acc.push(<span key={`sep-${i}`} className={styles.separator}> · </span>);
        acc.push(part);
        return acc;
      }, [])}
    </Text>
  );
};

const EnvironmentRow = ({ env, projectUrl }: { env: GitLabEnvironment; projectUrl?: string }) => {
  const deployment = env.lastDeployment;
  const envGitlabUrl = projectUrl ? `${projectUrl}/-/environments/${env.id}` : undefined;

  return (
    <Box className={styles.envRow}>
      <Flex direction="column" gap="2">
        <Flex gap="2" align="baseline">
          {deployment && <StatusDot status={deployment.status} />}
          {envGitlabUrl ? (
            <Link href={envGitlabUrl} target="_blank" variant="body-medium" weight="bold">{env.name}</Link>
          ) : (
            <Text variant="body-medium" weight="bold">{env.name}</Text>
          )}
          {env.externalUrl && (
            <Link href={env.externalUrl} target="_blank" variant="body-small" color="secondary">
              {new URL(env.externalUrl).hostname}
            </Link>
          )}
        </Flex>

        {deployment && (
          <Box className={styles.deploymentIndent}>
            <DeploymentMeta deployment={deployment} />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export const GitLabEnvironmentsCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { projectUrl } = useSourceLocation();

  const environmentsAnnotation =
    entity.metadata.annotations?.[GITLAB_ANNOTATIONS.ENVIRONMENTS];
  const environments = parseEnvironmentsAnnotation(environmentsAnnotation);

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <Text variant="title-small" weight="bold">
            {t('gitlab.environments.title')}
          </Text>
          {environments && environments.length > 0 && (() => {
            const label = `${environments.length} ${environments.length === 1 ? t('gitlab.environments.environment') : t('gitlab.environments.environments')}`;
            const environmentsUrl = projectUrl ? `${projectUrl}/-/environments` : undefined;
            return environmentsUrl
              ? <Link href={environmentsUrl} target="_blank" variant="body-small" color="secondary">{label}</Link>
              : <Text variant="body-small" color="secondary">{label}</Text>;
          })()}
        </Flex>
      </CardHeader>
      <CardBody>
        {!environments || environments.length === 0 ? (
          <EmptyState
            title={t('gitlab.environments.emptyState.title')}
            description={t('gitlab.environments.emptyState.description')}
          />
        ) : (
          <Flex direction="column" gap="2">
            {environments.map(env => (
              <EnvironmentRow key={env.id} env={env} projectUrl={projectUrl} />
            ))}
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
